import { Dropdown, Menu } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import {
  IconButton,
  BeatNIcon as beatNIcon,
  BeatQIcon as beatQIcon,
  BeatSIcon as beatSIcon,
  BeatSwapTemplate as beatSwapTemplateIcon,
  BeatVIcon as beatVIcon,
  BlackDeleteIcon as blackDeleteIcon,
  BlackUndoIcon as blackUndoIcon,
  EcgToolbarArrowIcon as ToolbarArrowIcon,
  ReviewedIcon16 as reviewMarkIcon,
  UnreviewedIcon as reviewUnmarkIcon,
} from '@/shared/ui';
import { REVIEWED_BEAT_FILTER, SORT_BEATS_OPTIONS, TYPE_BEAT_ENUM } from '@/shared/config';
import { UncontrolledTooltip } from 'reactstrap';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import BulkAction from './bulkAction';
import BulkActionByCell from './bulkActionByCell';
import BeatThumbnailConfig from './thumbnailConfig/beatThumbnailConfig';
import { hasTemplateBeat } from './reviewBeatsHelper';
import RrPaginationContainer from './rrPaginationContainer';
import useRrHeatmapButtonTypes from './useRrHeatmapButtonTypes';

/**
 * Renders the R-R heatmap toolbar covering beat reclassification, review and bulk actions. Offers N, S, V and Q swap buttons plus delete and review toggles wired to the hook handlers, adds a swap-template button only for template beat types, shows the reset control when changes exist, and appends the sort and filter dropdowns, bulk-action panels and pagination when beat actions apply.
 *
 * @returns The rendered R-R heatmap toolbar row.
 */
const RrHeatmapButtonTypes = () => {
  const {
    keyRecoil,
    activeButton,
    isLoadingStrip,
    isDisableButton,
    isDisableChangingButton,
    isDisableCommonButton,
    isShowResetButton,
    isShowBeatAction,
    isShowSort,
    isBeatsReviewed,
    visibleReviewedFilter,
    beatsSortType,
    handleChangeStripType,
    handleChangeBeatReview,
    onClickResetBeat,
    handleClickSwapBeats,
    onChangeFilter,
    onChangeSort,
  } = useRrHeatmapButtonTypes();

  /**
   * Builds the reviewed-beats filter dropdown for the R-R heatmap toolbar. Turns the reviewed-beat filter options into menu items, marks the current filter as selected, and disables the trigger while strips are loading.
   *
   * @returns The rendered reviewed-beats filter dropdown.
   */
  const renderFilterDropdown = () => {
    const items: any[] = [];
    _.forEach(REVIEWED_BEAT_FILTER, (x, i) =>
      items.push({
        key: `filter-${i}`,
        label: (
          <div
            id={`beats-filter-${i}`}
            className={classNames(x === visibleReviewedFilter ? 'selected' : '')}
          >
            {x}
          </div>
        ),
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isLoadingStrip}
        trigger={['click']}
        overlay={<Menu onClick={onChangeFilter} className="thumbnail-size-menu" items={items} />}
      >
        <button id="beats-rr-heatmap-filter-btn" className="thumbnail-size-text">
          {visibleReviewedFilter}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  /**
   * Builds the beat sort dropdown for the R-R heatmap toolbar. Turns the sort options into menu items, marks the current sort type as selected, and disables the trigger while strips are loading.
   *
   * @returns The rendered beat sort dropdown.
   */
  const renderSortDropdown = () => {
    const items: any[] = [];
    _.forEach(SORT_BEATS_OPTIONS, (x, i) =>
      items.push({
        key: `sort-${i}`,
        label: (
          <div id={`beats-sort-${i}`} className={classNames(x === beatsSortType ? 'selected' : '')}>
            {x}
          </div>
        ),
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isLoadingStrip}
        trigger={['click']}
        overlay={<Menu onClick={onChangeSort} className="thumbnail-size-menu" items={items} />}
      >
        <button id="beats-rr-heatmap-sort-btn" className="thumbnail-size-text">
          {beatsSortType}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  return (
    <div
      id="rr-heatmap-button-types"
      className={classNames(
        'beat-type-strip-content-top-container rr-heatmap-button-action-container',
        '--min-width-strip-rr-heat-map',
      )}
    >
      <div className="left-section">
        <div
          className={classNames(
            'toolbar-container',
            hasTemplateBeat(activeButton) ? '' : '--no-template-beat',
          )}
        >
          <div className="beat-action-title">Beat tools</div>
          <div className="toolbar">
            <IconButton
              isOutline
              id="beatN"
              iconComponent={<img src={beatNIcon} alt="Swap beat N icon" />}
              disabled={isDisableButton}
              onClick={() => handleChangeStripType(TYPE_BEAT_ENUM.N)}
            />
            <IconButton
              isOutline
              id="beatS"
              iconComponent={<img src={beatSIcon} alt="Swap beat S icon" />}
              disabled={isDisableButton}
              onClick={() => handleChangeStripType(TYPE_BEAT_ENUM.S)}
            />
            <IconButton
              isOutline
              id="beatV"
              iconComponent={<img src={beatVIcon} alt="Swap beat V icon" />}
              disabled={isDisableButton}
              onClick={() => handleChangeStripType(TYPE_BEAT_ENUM.V)}
            />
            <IconButton
              isOutline
              id="beatQ"
              iconComponent={<img src={beatQIcon} alt="Swap beat Q icon" />}
              disabled={isDisableButton}
              onClick={() => handleChangeStripType(TYPE_BEAT_ENUM.Q)}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="beatN"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
              trigger="hover"
            >
              <div className="beat-tooltip">
                Change to N-beat
                <span className="shortkey">N</span>
              </div>
            </UncontrolledTooltip>
            <UncontrolledTooltip
              className="custom-tooltip"
              target="beatS"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
              trigger="hover"
            >
              <div className="beat-tooltip">
                Change to S-beat
                <span className="shortkey">S</span>
              </div>
            </UncontrolledTooltip>
            <UncontrolledTooltip
              className="custom-tooltip"
              target="beatV"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
              trigger="hover"
            >
              <div className="beat-tooltip">
                Change to V-beat
                <span className="shortkey">V</span>
              </div>
            </UncontrolledTooltip>
            <UncontrolledTooltip
              className="custom-tooltip"
              target="beatQ"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
              trigger="hover"
            >
              <div className="beat-tooltip">
                Change to Q-beat
                <span className="shortkey">Q</span>
              </div>
            </UncontrolledTooltip>
            <IconButton
              id="btnDeleteBeatHr"
              isOutline
              iconComponent={<img src={blackDeleteIcon} alt="Delete icon" />}
              onClick={() => handleChangeStripType(TYPE_BEAT_ENUM.DELETED)}
              disabled={isDisableChangingButton}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnDeleteBeatHr"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
              trigger="hover"
            >
              <div className="beat-tooltip">
                Delete beat
                <span className="shortkey">D</span>
              </div>
            </UncontrolledTooltip>

            <IconButton
              id="btnMarkReviewBeatRR"
              isOutline
              iconComponent={
                <img
                  src={isBeatsReviewed ? reviewMarkIcon : reviewUnmarkIcon}
                  alt="Review mark icon"
                />
              }
              onClick={() => handleChangeBeatReview({ isReviewed: !isBeatsReviewed })}
              disabled={isDisableChangingButton}
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnMarkReviewBeatRR"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
              trigger="hover"
            >
              <div className="beat-tooltip">
                {isBeatsReviewed ? 'Mark beat as not reviewed' : 'Mark beat as reviewed'}
                <span className="shortkey">R</span>
              </div>
            </UncontrolledTooltip>

            {hasTemplateBeat(activeButton) && (
              <>
                <IconButton
                  isOutline
                  id="swapBeatChanges"
                  iconComponent={<img src={beatSwapTemplateIcon} alt="Swap beat template icon" />}
                  disabled={isDisableButton}
                  onClick={handleClickSwapBeats}
                />
                <UncontrolledTooltip
                  className="custom-tooltip"
                  target="swapBeatChanges"
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                  trigger="hover"
                >
                  <div className="beat-tooltip">Swap template</div>
                </UncontrolledTooltip>
              </>
            )}
            {!!isShowResetButton && (
              <>
                <div className="btn-type-vertical-line" />
                <IconButton
                  id="btnResetBeatHr"
                  isOutline
                  iconComponent={<img src={blackUndoIcon} alt="Noise icon" />}
                  disabled={isDisableCommonButton}
                  onClick={onClickResetBeat}
                />
                <UncontrolledTooltip
                  className="custom-tooltip"
                  target="btnResetBeatHr"
                  placement="bottom"
                  delay={{ show: 0, hide: 0 }}
                  hideArrow
                  trigger="hover"
                >
                  <div className="beat-tooltip">Reset changes</div>
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>
        <BeatThumbnailConfig isDisabled={isLoadingStrip} activeButton={activeButton} />
        {isShowBeatAction && (
          <div className="beat-bulk-action-container">
            <div className="beat-action-title">Bulk actions...</div>
            <div className="beat-bulk-action">
              <BulkActionByCell
                isTemplate={_.includes(['V', 'S'], activeButton as any)}
                isDisabled={isLoadingStrip}
                keyRecoil={keyRecoil}
              />
              <BulkAction
                isTemplate={_.includes(['V', 'S'], activeButton as any)}
                isDisabled={isLoadingStrip}
                keyRecoil={keyRecoil}
                tabId={BEAT_HR_TAB_ID}
              />
            </div>
          </div>
        )}
        {isShowSort && (
          <div className="beat-bulk-action-container">
            <div className="beat-action-title">Sort by</div>
            <div className="beat-bulk-action">{renderSortDropdown()}</div>
          </div>
        )}
        {isShowBeatAction && (
          <div className="beat-bulk-action-container">
            <div className="beat-action-title">Reviewed beats</div>
            <div className="beat-bulk-action">{renderFilterDropdown()}</div>
          </div>
        )}
      </div>
      {isShowBeatAction && <RrPaginationContainer isDisabled={isLoadingStrip} />}
    </div>
  );
};

export default RrHeatmapButtonTypes;
